import React, { forwardRef, useCallback, useContext, useEffect, useImperativeHandle, useRef } from 'react';

import { TransactionGMetrixTable } from 'components/TransactionGMetrixTable/TransactionGMetrixTable';
import { MostRestrictiveLicense, Transaction, TransactionApi } from 'apis/TransactionApi';
import { GMetrixDialog } from 'components/GMetrixDialog/GMetrixDialog';
import { TransactionDetailsContext } from 'pages/access_codes/context';
import { getMostRestrictedLicenseDeliverable } from 'utils/objects';
import { FormattedMessage, useIntl } from 'react-intl';
import { StudentGroupApi } from 'apis/StudentGroupApi';
import { Col, Container, Row } from 'react-bootstrap';
import { SortDescriptor } from 'utils/dataQuery';
import { ReportFilter } from 'apis/ReportApi';
import { Student } from 'apis/StudentApi';
import { useSuperState } from 'hooks';
import { ListItemCount } from 'types';

import 'components/TransactionTableGMetrixDialog/transaction-gmetrix-table-dialog.scss';

type Option = 'Add' | 'Remove' | undefined;

export interface TransactionAssociation {
    associationIds: number[];
    associationName: 'Student' | 'Group' | 'Report';
}

export interface TransactionGMetrixTableDialogShowOptions {
    actionButtonText?: string;
    customDialogLabel?: string;
    filter?: ReportFilter;
    multiSelect?: boolean;
    objectId?: number;
    option?: Option;
    removeTheseTransactions?: number[];
    transactionAssociation?: TransactionAssociation;
}

export interface TransactionGMetrixTableDialogResult {
    ids: number[];
    transactions: Transaction[];
}

export interface TransactionGMetrixTableDialogHandle {
    hide: () => void;
    show: (overrides?: TransactionGMetrixTableDialogShowOptions) => Promise<TransactionGMetrixTableDialogResult | null>;
}

interface TransactionGMetrixTableDialogProps {
    actionButtonText?: string;
    currentUserId: number;
    customDialogLabel?: string;
    filter?: ReportFilter;
    multiSelect?: boolean;
    neverDisableSuccessBtn?: boolean;
    objectId?: number;
    option?: Option;
    removeTheseTransactions?: number[];
    setTransactionKendoCount?: React.Dispatch<React.SetStateAction<ListItemCount>>;
    transactionAssociation?: TransactionAssociation;
}

export const TransactionGMetrixTableDialog = forwardRef<TransactionGMetrixTableDialogHandle, TransactionGMetrixTableDialogProps>((props, forwardedRef) => {

    const {
        actionButtonText: propActionButtonText,
        currentUserId,
        customDialogLabel: propCustomDialogLabel,
        filter: propFilter,
        multiSelect: propMultiSelect,
        neverDisableSuccessBtn = false,
        objectId: propObjectId,
        option: propOption,
        removeTheseTransactions: propRemoveTheseTransactions,
        setTransactionKendoCount,
        transactionAssociation: propTransactionAssociation
    } = props;

    const [state, setState] = useSuperState({
        loadingGridData: true,
        overrides: null as TransactionGMetrixTableDialogShowOptions | null,
        paginationSkip: 0,
        paginationTake: 15,
        restrictedLicenseObj: { restriction: undefined as MostRestrictiveLicense | undefined, show: false },
        searchTerm: '',
        selectedTransactions: [] as Transaction[],
        serverTotalCount: 0,
        sortDir: '',
        sortField: '',
        transactions: [] as Transaction[]
    });
    const {
        loadingGridData, overrides, paginationSkip, paginationTake, restrictedLicenseObj,
        searchTerm, selectedTransactions, serverTotalCount, sortDir, sortField, transactions
    } = state;
    const isOpen = overrides !== null;

    const { pageContext } = useContext(TransactionDetailsContext);
    const { formatMessage } = useIntl();

    const resolverRef = useRef<((value: TransactionGMetrixTableDialogResult | null) => void) | null>(null);
    const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const isInitialSearchRef = useRef(true);

    const resolvedOption = overrides?.option ?? propOption;
    const resolvedMultiSelect = overrides?.multiSelect ?? propMultiSelect;
    const resolvedObjectId = overrides?.objectId ?? propObjectId;
    const resolvedRemoveTheseTransactions = overrides?.removeTheseTransactions ?? propRemoveTheseTransactions;
    const resolvedAssociation = overrides?.transactionAssociation ?? propTransactionAssociation;
    const resolvedFilter = overrides?.filter ?? propFilter;
    const resolvedActionButtonText = overrides?.actionButtonText ?? propActionButtonText ?? '';
    const resolvedCustomDialogLabel = overrides?.customDialogLabel ?? propCustomDialogLabel;

    const isAddMode = resolvedOption === 'Add';

    /** Settle the promise, reset state, and close the dialog. */
    const settle = useCallback((value: TransactionGMetrixTableDialogResult | null) => {

        const resolve = resolverRef.current;
        resolverRef.current = null;
        setState({
            loadingGridData: true,
            overrides: null,
            paginationSkip: 0,
            restrictedLicenseObj: { restriction: undefined, show: false },
            searchTerm: '',
            selectedTransactions: [],
            serverTotalCount: 0,
            sortDir: '',
            sortField: '',
            transactions: []
        });
        isInitialSearchRef.current = true;
        resolve?.(value);

    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useImperativeHandle(forwardedRef, () => ({
        hide: () => settle(null),
        show: (nextOverrides?: TransactionGMetrixTableDialogShowOptions) => new Promise<TransactionGMetrixTableDialogResult | null>(resolve => {

            // Cancel any in-flight pick.
            resolverRef.current?.(null);
            resolverRef.current = resolve;
            setState({ overrides: nextOverrides ?? {} });

        })
    }), [settle]); // eslint-disable-line react-hooks/exhaustive-deps

    // Resolve pending promise on unmount.
    useEffect(() => () => {

        resolverRef.current?.(null);
        resolverRef.current = null;

    }, []);

    // ----------------------------------------------
    //  Server-side pagination (Add mode)
    // ----------------------------------------------

    const fetchTransactionsPaged = async (
        skip: number,
        take: number,
        sField: string,
        sDir: string,
        search: string
    ) => {

        if (!isOpen) return;
        setState({ loadingGridData: true });
        const page = Math.floor(skip / take) + 1;

        try {

            const result = await TransactionApi.GetAllPaged(page, take, sField, sDir, search);
            let items = result.Items;
            if (resolvedRemoveTheseTransactions) {

                items = items.filter(transaction => !resolvedRemoveTheseTransactions.includes(transaction.Id));

            }
            setState({ serverTotalCount: result.TotalCount, transactions: items });

        } catch (e) {

            console.error(e);

        } finally {

            setState({ loadingGridData: false });

        }

    };

    const handleServerPageChange = (skip: number, take: number) => {

        setState({ paginationSkip: skip, paginationTake: take });
        fetchTransactionsPaged(skip, take, sortField, sortDir, searchTerm);

    };

    const handleServerSortChange = (sort: SortDescriptor[]) => {

        const newSortField = sort.length > 0 ? sort[0].field : '';
        const newSortDir = sort.length > 0 ? (sort[0].dir ?? '') : '';
        setState({ paginationSkip: 0, sortDir: newSortDir, sortField: newSortField });
        fetchTransactionsPaged(0, paginationTake, newSortField, newSortDir, searchTerm);

    };

    // Debounce search term for server-side pagination.
    useEffect(() => {

        if (!isOpen || !isAddMode) return;

        if (isInitialSearchRef.current) {

            isInitialSearchRef.current = false;
            return;

        }

        if (searchDebounceRef.current) {

            clearTimeout(searchDebounceRef.current);

        }
        searchDebounceRef.current = setTimeout(() => {

            setState({ paginationSkip: 0 });
            fetchTransactionsPaged(0, paginationTake, sortField, sortDir, searchTerm);

        }, 1000);

        return () => {

            if (searchDebounceRef.current) {

                clearTimeout(searchDebounceRef.current);

            }

        };

    }, [searchTerm]); // eslint-disable-line react-hooks/exhaustive-deps

    // ----------------------------------------------
    //  Load grid data on open
    // ----------------------------------------------

    useEffect(() => {

        if (!isOpen) {

            setState({
                loadingGridData: false,
                restrictedLicenseObj: { restriction: undefined, show: false },
                selectedTransactions: [],
                transactions: []
            });
            return;

        }

        setState({ loadingGridData: true });

        if (isAddMode) {

            fetchTransactionsPaged(0, paginationTake, sortField, sortDir, '');

        } else {

            handleGMetrixTableData(resolvedOption);

        }

    }, [isOpen, resolvedOption]); // eslint-disable-line react-hooks/exhaustive-deps

    // Pre-select rows matching filter.Value CSV.
    useEffect(() => {

        if (resolvedFilter && resolvedFilter.Value && resolvedFilter.Value.length > 0 && transactions.length > 0) {

            const filterSelectedGroups: Transaction[] = [];
            const nonCsvListOfIds = resolvedFilter.Value.split(',');
            nonCsvListOfIds.forEach(filterValueId => {

                const transactionExists = transactions.find(transaction => transaction.Id === parseInt(filterValueId));
                if (transactionExists) {

                    filterSelectedGroups.push(transactionExists);

                }

            });
            if (filterSelectedGroups.length > 0) {

                setState({ selectedTransactions: filterSelectedGroups });

            }

        }

    }, [transactions, resolvedFilter]); // eslint-disable-line react-hooks/exhaustive-deps

    // ----------------------------------------------
    //  Non-paged data load (Remove mode only)
    // ----------------------------------------------

    const handleGMetrixTableData = async (option: Option) => {

        if (!isOpen) return;

        const transactionListInTable: any[] = [];
        let listOfTransactions: Transaction[] = [];

        if (option === 'Remove' && resolvedAssociation) {

            for (const id of resolvedAssociation.associationIds) {

                const fetched = resolvedAssociation.associationName === 'Student'
                    ? await TransactionApi.GetStudentTransactions(id)
                    : await StudentGroupApi.GetTransactionsFromGroup(id);
                transactionListInTable.push(...fetched);

            }

            if (resolvedAssociation.associationName === 'Student') {

                listOfTransactions = [...new Map(transactionListInTable.map(item => [item.TransactionId, item])).values()];

            } else {

                listOfTransactions = [...new Map(transactionListInTable.map(item => [item.Id, item])).values()];

            }

        }

        setState({ transactions: listOfTransactions });
        if (setTransactionKendoCount && listOfTransactions.length > 0) {

            setTransactionKendoCount({ count: 1, firstId: listOfTransactions[0].Id });

        }
        setState({ loadingGridData: false });

    };

    const runNonReportConfirm = async (option: Option) => {

        if (!resolvedAssociation) return;

        const transactionIds = selectedTransactions.map(transaction => transaction.Id);
        const confirmPayload: TransactionGMetrixTableDialogResult = { ids: transactionIds, transactions: selectedTransactions };

        if (option === 'Add') {

            // Compute the most-restrictive license.
            let mostRestrictedLicense: MostRestrictiveLicense | undefined;
            const licenseObj = await TransactionApi.GetAllLicenses().catch(console.error);
            for (const transactionId of transactionIds) {

                const content = await TransactionApi.GetLimitedContentByTransactionId(transactionId).catch(console.error);
                if (licenseObj && content) {

                    const restrictedLicense = getMostRestrictedLicenseDeliverable(
                        licenseObj.licenses,
                        content,
                        pageContext?.Data.transactionDetails?.HasXED === true
                    );
                    if (mostRestrictedLicense === undefined) {

                        mostRestrictedLicense = restrictedLicense;

                    } else if (
                        mostRestrictedLicense.avaliableStudentsAllowed === -1
                        && restrictedLicense.avaliableStudentsAllowed !== -1
                    ) {

                        mostRestrictedLicense = restrictedLicense;

                    } else if (
                        mostRestrictedLicense.avaliableStudentsAllowed < restrictedLicense.avaliableStudentsAllowed
                        && restrictedLicense.avaliableStudentsAllowed !== -1
                    ) {

                        mostRestrictedLicense = restrictedLicense;

                    }

                }

            }

            if (resolvedAssociation.associationName === 'Group') {

                let students: Student[] = [];
                for (const groupId of resolvedAssociation.associationIds) {

                    const groupStudents = await StudentGroupApi.GetAllStudents(groupId).catch(console.error);
                    if (groupStudents) students = [...students, ...groupStudents];

                }
                if (
                    mostRestrictedLicense
                    && (students.length <= mostRestrictedLicense.avaliableStudentsAllowed
                        || mostRestrictedLicense.avaliableStudentsAllowed === -1)
                ) {

                    await TransactionApi.AddGroupToTransaction(transactionIds, resolvedAssociation.associationIds);

                } else {

                    // Stay open; user can adjust selection.
                    setState({ restrictedLicenseObj: { restriction: mostRestrictedLicense, show: true } });
                    return;

                }

            }
            if (resolvedAssociation.associationName === 'Student') {

                if (
                    mostRestrictedLicense
                    && (resolvedAssociation.associationIds.length <= mostRestrictedLicense.avaliableStudentsAllowed
                        || mostRestrictedLicense.avaliableStudentsAllowed === -1)
                ) {

                    TransactionApi.AddStudentToTransaction(transactionIds, resolvedAssociation.associationIds).catch(console.error);

                } else {

                    setState({ restrictedLicenseObj: { restriction: mostRestrictedLicense, show: true } });
                    return;

                }

            }

        } else if (option === 'Remove') {

            if (resolvedAssociation.associationName === 'Group') {

                await StudentGroupApi.DeleteTransactionFromGroup(transactionIds, resolvedAssociation.associationIds);

            }
            if (resolvedAssociation.associationName === 'Student') {

                const studentContentAccessIdsToRemove: number[] = [];
                for (const studentId of resolvedAssociation.associationIds) {

                    const studentsTransactions = await TransactionApi.GetStudentTransactions(studentId);
                    if (studentsTransactions.length > 0) {

                        transactionIds.forEach(transactionId => {

                            const transactionExists = studentsTransactions.find(studentTransaction => studentTransaction.Id === transactionId);
                            if (transactionExists) {

                                studentContentAccessIdsToRemove.push(transactionExists.StudentContentAccessId);

                            }

                        });

                    }

                }
                if (studentContentAccessIdsToRemove.length > 0) {

                    await TransactionApi.RemoveStudentFromTransaction(studentContentAccessIdsToRemove);

                }

            }

        }

        settle(confirmPayload);

    };

    const handleConfirm = () => {

        if (resolvedAssociation?.associationName === 'Report') {

            settle({
                ids: selectedTransactions.map(t => t.Id),
                transactions: selectedTransactions
            });
            return;

        }

        runNonReportConfirm(resolvedOption);

    };

    const dialogLabel = resolvedCustomDialogLabel
        ? resolvedCustomDialogLabel
        : `${resolvedOption ?? ''} ${formatMessage({ id: 'access_codes.3114ae04', defaultMessage: 'Access Codes' })}`;

    const buttonText = neverDisableSuccessBtn && selectedTransactions.length === 0
        ? formatMessage({ id: 'clear_all.bb234ae5', defaultMessage: 'Clear All' })
        : resolvedActionButtonText;

    return (
        <GMetrixDialog
            actionButtonClick={handleConfirm}
            actionButtonDisabled={selectedTransactions.length === 0 && !neverDisableSuccessBtn}
            actionButtonText={buttonText}
            dialogLabel={dialogLabel}
            onCancel={() => settle(null)}
            show={isOpen}
        >
            <Container fluid className="transaction-gmetrix-table-dialog">
                {restrictedLicenseObj && restrictedLicenseObj.restriction && restrictedLicenseObj.show && (
                    <>
                        <Row>
                            <Col>
                                <div className="py-2 d-flex justify-content-center">
                                    <FormattedMessage defaultMessage="Error" id="error.e50d69ad" />
                                    {' '}
                                    -
                                    {restrictedLicenseObj.restriction.license.LicenseName}
                                    {' '}
                                    <FormattedMessage defaultMessage="is being used for some of the content in this Access Code and it only has" id="is_being_used_for_some_of_the_content_in.fedeb56b" />
                                    {restrictedLicenseObj.restriction.avaliableStudentsAllowed}
                                    {' '}
                                    <FormattedMessage defaultMessage="seats available" id="seats_available.d1ad13fb" />
                                    .
                                </div>
                                <div className="py-2 d-flex justify-content-center">
                                    <FormattedMessage defaultMessage="You can only add up to" id="you_can_only_add_up_to.5038baab" />
                                    {' '}
                                    {restrictedLicenseObj.restriction.avaliableStudentsAllowed}
                                    <FormattedMessage defaultMessage="students to this Access Code." id="students_to_this_access_code.92972378" />
                                </div>
                            </Col>
                        </Row>
                        <hr className="hr" />
                    </>
                )}
                <Row>
                    <Col className="pb-3">
                        <TransactionGMetrixTable
                            manuallyRenderTransactions
                            selectableRows
                            columnData={[
                                { field: 'Name', title: formatMessage({ id: 'name.5458f379', defaultMessage: 'Name' }) },
                                { field: 'NumOfStudents', title: formatMessage({ id: 'number_of_students.24d788d2', defaultMessage: 'Number of Students' }) },
                                { field: 'Groups', title: formatMessage({ id: 'classrooms.e66138fb', defaultMessage: 'Classrooms' }) },
                                { field: 'Instructor', title: formatMessage({ id: 'instructor.845e6d37', defaultMessage: 'Instructor' }) },
                                { field: 'CreatedDate', title: formatMessage({ id: 'date_created.85efe6f3', defaultMessage: 'Date Created' }) },
                                { field: 'ExpirationDate', title: formatMessage({ id: 'end_date.10ec21c1', defaultMessage: 'End Date' }) }
                            ]}
                            transactions={isAddMode ? transactions : transactions.filter(transaction => {

                                const lowerSearch = searchTerm.toLowerCase();
                                return (
                                    transaction.Name.toLowerCase().includes(lowerSearch)
                                    || transaction.CreatedDateString.toLowerCase().includes(lowerSearch)
                                    || (transaction.ExpirationDateString && transaction.ExpirationDateString.toLowerCase().includes(lowerSearch))
                                    || (transaction.Instructor && transaction.Instructor.toLowerCase().includes(lowerSearch))
                                );

                            })}
                            allowExport={false}
                            currentUserId={currentUserId}
                            loadingGridData={loadingGridData}
                            multiSelect={resolvedMultiSelect}
                            onSelectedChange={transactions => setState({ selectedTransactions: transactions })}
                            onServerPageChange={isAddMode ? handleServerPageChange : undefined}
                            onServerSortChange={isAddMode ? handleServerSortChange : undefined}
                            searchTerm={searchTerm}
                            selectedTransactions={selectedTransactions}
                            serverSidePagination={isAddMode}
                            serverTotalCount={isAddMode ? serverTotalCount : undefined}
                            setLoadingGridData={((v: boolean) => setState({ loadingGridData: v })) as React.Dispatch<React.SetStateAction<boolean>>}
                            setSearchTerm={term => setState({ searchTerm: term })}
                            setTransactions={transactions => setState({ transactions })}
                            toggleColumns={false}
                        />
                    </Col>
                </Row>
            </Container>
        </GMetrixDialog>
    );

});

TransactionGMetrixTableDialog.displayName = 'TransactionGMetrixTableDialog';
